/**
 * Shared File Snap row pieces (BM-H4295), used by the all-matters File Snap
 * page and the matter page's File Snap card so the two read the same.
 *
 * Vanessa, 13 Oct 2026: "type - use icons eg telephone for meeting or phone
 * attendance, envelope for email, file for manual update ... we're looking for
 * max usability at speed". The icon carries the type; the label is in the
 * tooltip and for screen readers.
 */
import {
  Phone, Mail, FileText, Inbox, Send, Clock, ClipboardList, ShieldCheck,
  FileSignature, Scale, Bell, Activity, StickyNote, UserPlus, Sparkles,
} from "lucide-react";

const ATTENDANCE_CHANNEL = {
  telephone: "Telephone attendance",
  teams: "Meeting (Teams)",
  zoom: "Meeting (Zoom)",
  in_person: "Meeting (in person)",
  other: "Attendance",
};

// [icon, label] per touchpoint category (hub/file_snap_touchpoints.py).
const TOUCHPOINT = {
  email_filed: [Mail, "Email filed"],
  email_sent: [Mail, "Email sent"],
  onboarding: [UserPlus, "Onboarding"],
  conflict: [Scale, "Conflict check"],
  aml: [ShieldCheck, "ID / AML"],
  ccl: [FileSignature, "CCL"],
  form: [ClipboardList, "Form"],
  time: [Clock, "Time"],
  post_in: [Inbox, "Post in"],
  post_out: [Send, "Post out"],
  staff_alert: [Bell, "Staff alert"],
  activity: [Activity, "Activity"],
  matter_note: [StickyNote, "Matter note"],
};

export function fileSnapType(row) {
  if (!row) return [FileText, "Entry"];
  if (row.type === "attendance_note") {
    return [Phone, ATTENDANCE_CHANNEL[row.channel] || "Attendance"];
  }
  if (row.type === "manual_file_note") return [FileText, "File note"];
  if (row.type === "matter_note") return [StickyNote, "Matter note"];
  if (row.type === "touchpoint") return TOUCHPOINT[row.category] || [Activity, row.category_label || "Activity"];
  return [FileText, row.type || "Entry"];
}

export function FileSnapTypeIcon({ row, className = "w-4 h-4" }) {
  const [Icon, label] = fileSnapType(row);
  const tone = row?.type === "touchpoint" ? "text-slate-400" : "text-teal";
  return (
    <span className={`inline-flex items-center ${tone}`} title={label} data-testid="file-snap-type-icon"
          data-type-label={label}>
      <Icon className={className} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

// The one line to show: the AI summary when the note has one, else the
// cleaned text the backend sends, else the raw note (touchpoints, old API).
export function snapSummary(row) {
  return (row?.note_summary || row?.note_text || "").trim();
}

export function AiMark({ row }) {
  if (row?.note_summary_source !== "ai") return null;
  return (
    <span className="inline-flex items-center text-indigo-300 mr-1 align-[-2px]"
          title="Summary written by AI from the note. Open the full note to check it."
          data-testid="file-snap-ai-mark">
      <Sparkles className="w-3 h-3" aria-hidden="true" />
      <span className="sr-only">AI summary:</span>
    </span>
  );
}

// Everything a search box should match on a row.
export function rowHaystack(row, extra = []) {
  const [, label] = fileSnapType(row);
  return [
    row.matter_ref, row.client_name, row.fee_earner, row.fee_earner_name,
    row.party_name, row.note_summary, row.note_text, label, ...extra,
  ].filter(Boolean).join(" ").toLowerCase();
}
